import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, AUTH_EVENT } from '../lib/api.js';
import { load, save, clearUserData, artikelbilderLoeschen } from '../lib/storage.js';
import { snapshot as outboxEintraege } from '../lib/outbox.js';
import { vorgemerkteEntwuerfe } from '../lib/entwuerfe.js';

/** Noch nicht gespeicherte Buchungen und vorgemerkte Formulare auf diesem Gerät. */
const ausstehendeEintraege = () => outboxEintraege().filter((i) => !i.fehler).length + vorgemerkteEntwuerfe().length;

/**
 * Anmeldestatus. Die Session selbst ist ein HttpOnly-Cookie – das Frontend sieht keinen Token.
 * Meldet die API 401, erscheint die Anmeldung als Overlay über der App; danach geht es dort weiter, wo man war
 * (laufende Zeit und Eingaben bleiben erhalten).
 */
const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const qc = useQueryClient();
  const [required, setRequired] = useState(false);
  const [reason, setReason] = useState(null); // 'SESSION_EXPIRED' | 'LOGOUT' | null

  useEffect(() => {
    const onAuth = (e) => { setReason(e.detail?.code ?? null); setRequired(true); };
    window.addEventListener(AUTH_EVENT, onAuth);
    return () => window.removeEventListener(AUTH_EVENT, onAuth);
  }, []);

  const login = useCallback(async (benutzer, passwort, firma = '', { verwerfen = false } = {}) => {
    const me = await api('/auth/login', { method: 'POST', body: { benutzer, passwort, ...(firma ? { firma } : {}) } });
    const mandant = me.mandant?.id ?? null;
    // «standard» = bisheriger Betrieb ohne Firmen-id; dieselbe Person nach der Umbenennung (MANDANT_ID) ist kein Firmenwechsel
    const umbenannt = load('mandant', null) === 'standard' && load('uid', null) === me.uid;
    const letzteFirma = umbenannt ? mandant : load('mandant', null);
    const letzterBenutzer = load('benutzer', '');
    // Anderes Konto (andere Firma oder anderer Benutzer) auf diesem Gerät, aber vom bisherigen warten noch Buchungen
    // oder vorgemerkte Formulare: nicht stillschweigend löschen und nie unter dem neuen Konto (anderer Exxas-Tenant!)
    // senden – erst der bisherige Benutzer meldet sich an, oder es wird ausdrücklich verworfen.
    const wechsel = (letzteFirma && mandant && letzteFirma !== mandant) || (load('uid', null) && load('uid', null) !== me.uid);
    const offen = wechsel ? ausstehendeEintraege() : 0;
    if (offen && !verwerfen) {
      await api('/auth/logout', { method: 'POST' }).catch(() => {});
      const err = new Error(`Auf diesem Gerät warten noch ${offen === 1 ? '1 Eintrag' : `${offen} Einträge`} von ${letzterBenutzer || 'einem anderen Konto'}, die noch nicht gespeichert sind. Bitte zuerst mit diesem Konto anmelden – dann werden sie gesendet.`);
      err.code = 'AUSSTEHEND';
      throw err;
    }
    save('benutzer', benutzer);
    save('firma', firma || null);
    if (letzteFirma && mandant && letzteFirma !== mandant) {
      // Andere Firma auf diesem Gerät: nichts von der bisherigen bleibt (Aufträge, Artikelstamm, Bilder)
      clearUserData({ firmenwechsel: true });
      await artikelbilderLoeschen();
      save('benutzer', benutzer);
      save('firma', firma || null);
      save('mandant', mandant);
      save('uid', me.uid);
      window.location.reload();
      return me;
    }
    if (mandant) save('mandant', mandant);
    const last = load('uid', null);
    if (last && last !== me.uid) {
      // Anderer Benutzer auf diesem Gerät: lokale Daten (laufende Zeit, zuletzt geöffnet) gehören nicht ihm
      clearUserData();
      save('benutzer', benutzer);
      save('firma', firma || null);
      save('uid', me.uid);
      window.location.reload();
      return me;
    }
    save('uid', me.uid);
    setRequired(false);
    setReason(null);
    qc.setQueryData(['me'], { uid: me.uid, name: me.name, email: me.email, rollen: me.rollen ?? [], mandant: me.mandant });
    await qc.invalidateQueries();
    return me;
  }, [qc]);

  /** Wirft, wenn der Server die Session nicht beenden konnte – dann bleibt alles, wie es ist (sonst wäre das Cookie noch gültig). */
  const logout = useCallback(async () => {
    await api('/auth/logout', { method: 'POST' });
    clearUserData();
    qc.clear();
    setReason('LOGOUT');
    setRequired(true);
  }, [qc]);

  const value = useMemo(() => ({ required, reason, login, logout }), [required, reason, login, logout]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
